import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  Type,
  ViewChild,
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';

import {GateweeTableColumn} from '@gatewee/interfaces';
import {DocumentType} from '@gatewee/types/document';
import {
  allowedWithdrawalPixKeyTypeOptions,
  DomainSetting,
  DomainSettingKey,
} from '@gatewee/types/domain-setting';
import {PaymentMethod} from '@gatewee/types/payment';
import {
  RecipientSetting,
  RecipientSettingKey,
} from '@gatewee/types/recipient-setting';
import {BooleanParameterComponent} from './boolean/boolean-parameter.component';
import {DefaultProviderComponent} from './default-provider/default-provider.component';
import {EnabledProvidersComponent} from './enabled-providers/enabled-providers.component';
import {SettingsDialogComponent} from './settings-dialog/settings-dialog.component';
import {StringParameterComponent} from './string/string-parameter.component';
import {MobileBuildManifestParameterComponent} from './mobile-build-manifest/mobile-build-manifest-parameter.component';
import {
  GlobalPaymentSplitRulesComponent,
  GlobalPaymentSplitRulesDataService,
} from './global-payment-split-rules/global-payment-split-rules.component';
import {SplitWhitelistComponent} from './split-whitelist/split-whitelist.component';
import {TrackingParameterComponent} from './tracking/tracking-parameter.component';
import {GateweeCollectionConfigParameterComponent} from './gatewee-collection-config/gatewee-collection-config-parameter.component';
import {FeeBatchMinimumParameterComponent} from './fee-batch-minimum/fee-batch-minimum-parameter.component';
import {ProvidersBaseFeesParameterComponent} from './providers-base-fees/providers-base-fees-parameter.component';
import {DefaultRecipientSettingsParameterComponent} from './default-recipient-settings/default-recipient-settings-parameter.component';
import {AntifraudParameterComponent} from './antifraud/antifraud-parameter.component';
import {ProviderSettingsOverridesParameterComponent} from './provider-settings-overrides/provider-settings-overrides-parameter.component';
import {ProviderCapabilitiesOverrideParameterComponent} from './provider-capabilities-override/provider-capabilities-override-parameter.component';
import {FeeCollectionPolicyParameterComponent} from './fee-collection-policy/fee-collection-policy-parameter.component';
import {BankingInfoParameterComponent} from './banking-info/banking-info-parameter.component';

@Component({
  selector: 'app-gatewee-settings',
  templateUrl: './gatewee-settings.component.html',
  standalone: false,
})
export class GateweeSettingsComponent implements OnInit, OnChanges {
  @Input('id') id!: number;
  @Input() settings!: Array<RecipientSetting | DomainSetting>;
  @Input() editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  @Input() disableContentPage = false;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.settings.title';
  @Input() titlePath = 'features.settings.titlePath';
  @Input() getDialogData?: (
    id: number,
    setting: RecipientSetting | DomainSetting,
    editSettingFn: (
      id: number,
      setting: RecipientSetting | DomainSetting,
    ) => Observable<void>,
  ) => Record<string, unknown>;
  @Input() entity?: {
    id: number;
    name: string;
    document: string;
    representative?: {
      name?: string;
      email?: string;
      phone?: string;
    };
  };
  @Input() scope: 'domain' | 'recipient' = 'domain';
  /** When true, settings not returned by the API are shown as "Not configured" (default). When false, only API settings are listed. */
  @Input() showMissingSettings = false;
  @Input() recipientsService?: {
    findAll(): Observable<unknown[]>;
  };
  @Input()
  globalPaymentSplitRulesDataService?: GlobalPaymentSplitRulesDataService;

  settingComponents = {
    // Use wrapper pattern for page-only components (style, page, fees)
    style: SettingsDialogComponent,
    page: SettingsDialogComponent,
    fees: SettingsDialogComponent,
    // Dialog-only components (simple, single-purpose)
    paymentMethods: BooleanParameterComponent,
    allowNaturalPersonSignup: BooleanParameterComponent,
    allowLegalPersonSignup: BooleanParameterComponent,
    standaloneOperation: BooleanParameterComponent,
    mailer: StringParameterComponent,
    fqdn: StringParameterComponent,
    enabledProviders: EnabledProvidersComponent,
    defaultProvider: DefaultProviderComponent,
    complianceRequirements: BooleanParameterComponent,
    preApprovedWithdrawal: BooleanParameterComponent,
    payoutPolicy: StringParameterComponent,
    enabledCommunications: BooleanParameterComponent,
    retentionDefinition: StringParameterComponent,
    enablePaymentSplit: BooleanParameterComponent,
    enableAutoWithdrawal: BooleanParameterComponent,
    paymentSplitWhitelist: SplitWhitelistComponent,
    globalPaymentSplitRules: GlobalPaymentSplitRulesComponent,
    mobileBuildManifest: MobileBuildManifestParameterComponent,
    tracking: TrackingParameterComponent,
    enableAutoFeeCollection: BooleanParameterComponent,
    enableMasterProviderFeeDeduction: BooleanParameterComponent,
    enableProviderRouter: BooleanParameterComponent,
    feePlanV2: BooleanParameterComponent,
    gateweeCollectionConfig: GateweeCollectionConfigParameterComponent,
    feeBatchMinimumAmount: FeeBatchMinimumParameterComponent,
    providersBaseFees: ProvidersBaseFeesParameterComponent,
    defaultRecipientSettings: DefaultRecipientSettingsParameterComponent,
    antifraud: AntifraudParameterComponent,
    enableDomainWhatsapp: BooleanParameterComponent,
    providerSettingsOverrides: ProviderSettingsOverridesParameterComponent,
    providerCapabilitiesOverride:
      ProviderCapabilitiesOverrideParameterComponent,
    feeCollectionPolicy: FeeCollectionPolicyParameterComponent,
    globalWebhookUrl: StringParameterComponent,
    requireBankAccountApproval: BooleanParameterComponent,
    autoApproveRefunds: BooleanParameterComponent,
    enableLostTxBackfill: BooleanParameterComponent,
    enableSettlementIncomeBatch: BooleanParameterComponent,
    enableMarlimDirectLiquidation: BooleanParameterComponent,
    autoWithdrawalRetentionPct: StringParameterComponent,
    invoiceAmountLimit: StringParameterComponent,
    cashoutTransactionLimit: StringParameterComponent,
    dailyCashoutLimit: StringParameterComponent,
    bankingInfo: BankingInfoParameterComponent,
    onboardingApi: StringParameterComponent,
    onboardingSettings: DefaultRecipientSettingsParameterComponent,
    invoiceAmountMinimum: StringParameterComponent,
    addWithdrawalFeeToAmount: BooleanParameterComponent,
    pixCheckoutUrlEnabled: BooleanParameterComponent,
    allowedWithdrawalPixKeyTypes: BooleanParameterComponent,
    enableBankSlipPayment: BooleanParameterComponent,
  };

  @ViewChild('confirmEditDialog', {read: TemplateRef})
  confirmEditDialog!: TemplateRef<unknown>;

  @ViewChild('addParameterSelectDialog', {read: TemplateRef})
  addParameterSelectDialog!: TemplateRef<unknown>;

  /** Keys that have a form component and are not yet in settings; used for "Add parameter". */
  get availableKeysForAdd(): string[] {
    const known = this.getKnownSettingKeys();
    const componentKeys = Object.keys(this.settingComponents);
    const configuredKeys = new Set((this.settings ?? []).map(s => s.key));
    return known.filter(
      k => componentKeys.includes(k) && !configuredKeys.has(k),
    );
  }

  getRowClass = (row: {
    key: string;
    keyLabel: string;
    description: string;
    value: string;
    setting: RecipientSetting | DomainSetting;
  }): string => {
    return row.setting.default ? 'gw-row-default' : '';
  };

  tableData: {
    key: string;
    keyLabel: string;
    description: string;
    value: string;
    setting: RecipientSetting | DomainSetting;
  }[] = [];
  columns: GateweeTableColumn[] = [];

  /** Selected key in the "Add parameter" select dialog. */
  selectedKeyForAdd = '';

  constructor(
    private alerts: TuiAlertService,
    private translate: TranslateService,
    private dialogService: TuiDialogService,
  ) {}

  ngOnInit(): void {
    this.setupColumns();
    this.processSettings();
    this.translate.onLangChange.subscribe(() => this.processSettings());
    this.translate.onTranslationChange.subscribe(() => this.processSettings());
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings'] || changes['showMissingSettings']) {
      this.processSettings();
    }
  }

  private setupColumns(): void {
    this.columns = [
      {
        label: 'features.settings.table.columns.parameter',
        field: 'keyLabel',
        sortable: false,
        visible: true,
        alwaysVisible: true,
      },
      {
        label: 'features.settings.table.columns.description',
        field: 'description',
        sortable: false,
        visible: true,
        alwaysVisible: true,
      },
      {
        label: 'features.settings.table.columns.value',
        field: 'value',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.settings.table.columns.actions',
        field: 'actions',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        actions: [
          {
            key: 'edit',
            label: 'common.actions.edit',
          },
        ],
      },
    ];
  }

  private processSettings(): void {
    const settingsFromApi = this.settings ?? [];

    if (!this.showMissingSettings) {
      this.tableData = settingsFromApi.map(setting => ({
        key: setting.key,
        keyLabel: this.translateParameterField(setting.key, 'label'),
        description: this.translateParameterField(setting.key, 'description'),
        value: this.parseSettingValue(setting),
        setting,
      }));
      return;
    }

    const knownKeys = this.getKnownSettingKeys();
    if (!knownKeys.length) {
      this.tableData = [];
      return;
    }

    const mergedSettings: Array<RecipientSetting | DomainSetting> = [];
    for (const key of knownKeys) {
      const fromApi = settingsFromApi.find(s => s.key === key);
      mergedSettings.push(fromApi ?? this.createPlaceholderSetting(key));
    }

    this.tableData = mergedSettings.map(setting => ({
      key: setting.key,
      keyLabel: this.translateParameterField(setting.key, 'label'),
      description: this.translateParameterField(setting.key, 'description'),
      value: this.parseSettingValue(setting),
      setting,
    }));
  }

  /** Maps stored setting keys to i18n paths under `features.settings.parameters` (colons etc.). */
  private parameterI18nKey(key: string): string {
    return key === 'provider:fire' ? 'providerFire' : key;
  }

  /** Resolves label/description; returns translated text when available. */
  private translateParameterField(
    key: string,
    field: 'label' | 'description',
  ): string {
    const i18nKey = `features.settings.parameters.${this.parameterI18nKey(
      key,
    )}.${field}`;
    const translated = this.translate.instant(i18nKey);
    return translated !== i18nKey ? translated : key;
  }

  /** Returns the list of setting keys we track for the current scope (domain or recipient). */
  private getKnownSettingKeys(): string[] {
    if (this.scope === 'recipient') {
      return Object.values(RecipientSettingKey);
    }
    return Object.values(DomainSettingKey);
  }

  /** Creates a placeholder setting for a key that was not returned by the API (shown as default/not configured). For new settings we omit id so the backend create payload does not send it. */
  private createPlaceholderSetting(
    key: string,
  ): RecipientSetting | DomainSetting {
    if (this.scope === 'recipient') {
      return {
        recipient: {id: this.id} as RecipientSetting['recipient'],
        key,
        value: '',
        secret: false,
        default: true,
        createdAt: new Date(0),
        updatedAt: new Date(0),
      } as RecipientSetting;
    }
    return {
      domain: {id: this.id} as DomainSetting['domain'],
      key,
      value: '',
      createdAt: new Date(0),
      updatedAt: new Date(0),
      default: true,
    } as DomainSetting;
  }

  private parseSettingValue(setting: RecipientSetting | DomainSetting): string {
    if (setting.default) {
      return this.translate.instant('features.settings.notConfigured');
    }

    if (this.isObjectOrArray(setting.value)) {
      if (setting.key === 'mobileBuildManifest') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const appName =
            parsed?.appName ?? parsed?.slug ?? parsed?.scheme ?? '';
          return appName
            ? String(appName)
            : this.translate.instant('features.settings.multipleValues');
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'tracking') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const client = parsed?.client ?? {};
          const parts: string[] = [];
          if (client.ga4MeasurementId) parts.push('GA4');
          if (client.metaPixelId) parts.push('Meta');
          if (client.tiktokPixelId) parts.push('TikTok');
          return parts.length > 0
            ? parts.join(', ')
            : this.translate.instant('features.settings.multipleValues');
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'globalPaymentSplitRules') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const arr = Array.isArray(parsed) ? parsed : [];
          const count = arr.length;
          return count === 0
            ? this.translate.instant(
                'features.settings.parameters.globalPaymentSplitRules.noRules',
              )
            : this.translate.instant(
                'features.settings.parameters.globalPaymentSplitRules.rulesCount',
                {count},
              );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'bankingInfo') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const pix = parsed?.pixKey ? String(parsed.pixKey).slice(-4) : '';
          return pix
            ? this.translate.instant(
                'features.settings.parameters.bankingInfo.summary',
                {pixHint: pix},
              )
            : this.translate.instant('features.settings.multipleValues');
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'gateweeCollectionConfig') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const min = parsed?.batchMinimumAmount;
          const pix = parsed?.bankingInfo?.pixKey
            ? String(parsed.bankingInfo.pixKey).slice(-4)
            : '';
          return min !== undefined && min !== null
            ? this.translate.instant(
                'features.settings.parameters.gateweeCollectionConfig.summary',
                {amount: String(min), pixHint: pix ? `…${pix}` : '—'},
              )
            : this.translate.instant('features.settings.multipleValues');
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'providersBaseFees') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const obj = parsed && typeof parsed === 'object' ? parsed : {};
          const providers = Object.keys(obj).filter(k =>
            Array.isArray((obj as Record<string, unknown>)[k]),
          );
          let rows = 0;
          for (const k of providers) {
            rows += ((obj as Record<string, unknown[]>)[k] ?? []).length;
          }
          return this.translate.instant(
            'features.settings.parameters.providersBaseFees.summary',
            {providers: providers.length, rows},
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'fireOnboarding') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const email = parsed?.email ? String(parsed.email) : '';
          return (
            email || this.translate.instant('features.settings.multipleValues')
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'paytimeOnboarding') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const id = parsed?.subPaytimePlanId;
          return id !== undefined && id !== null
            ? this.translate.instant(
                'features.settings.parameters.paytimeOnboarding.summary',
                {id: String(id)},
              )
            : this.translate.instant('features.settings.multipleValues');
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'provider:fire') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const hasKey = Boolean(parsed?.apiKey);
          return hasKey
            ? this.translate.instant(
                'features.settings.parameters.providerFire.summaryConfigured',
              )
            : this.translate.instant(
                'features.settings.parameters.providerFire.summaryEmpty',
              );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'defaultRecipientSettings') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const n =
            parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? Object.keys(parsed).length
              : 0;
          return this.translate.instant(
            'features.settings.parameters.defaultRecipientSettings.summary',
            {count: n},
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'onboardingSettings') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const n =
            parsed && typeof parsed === 'object' && !Array.isArray(parsed)
              ? Object.keys(parsed).length
              : 0;
          return this.translate.instant(
            'features.settings.parameters.onboardingSettings.summary',
            {count: n},
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'antifraud') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const obj = parsed && typeof parsed === 'object' ? parsed : {};
          const enabled = Object.values(obj).filter(
            (v: unknown) =>
              v &&
              typeof v === 'object' &&
              (v as {enabled?: boolean}).enabled === true,
          ).length;
          return this.translate.instant(
            'features.settings.parameters.antifraud.summary',
            {count: enabled},
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'providerSettingsOverrides') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const obj = parsed && typeof parsed === 'object' ? parsed : {};
          return this.translate.instant(
            'features.settings.parameters.providerSettingsOverrides.summary',
            {count: Object.keys(obj).length},
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'providerCapabilitiesOverride') {
        try {
          const parsed =
            typeof setting.value === 'string'
              ? JSON.parse(setting.value)
              : setting.value;
          const obj = parsed && typeof parsed === 'object' ? parsed : {};
          return this.translate.instant(
            'features.settings.parameters.providerCapabilitiesOverride.summary',
            {count: Object.keys(obj).length},
          );
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      if (setting.key === 'feeCollectionPolicy') {
        try {
          const raw = setting.value;
          if (typeof raw === 'string') {
            const s = raw.trim();
            if (s === 'internal' || s === 'providerSplit') {
              return this.translate.instant(
                `features.settings.parameters.feeCollectionPolicy.simple.${s}`,
              );
            }
            const j = JSON.parse(s) as unknown;
            if (j === 'internal' || j === 'providerSplit') {
              return this.translate.instant(
                `features.settings.parameters.feeCollectionPolicy.simple.${j}`,
              );
            }
          }
          if (typeof raw === 'object' && raw !== null) {
            return this.translate.instant(
              'features.settings.parameters.feeCollectionPolicy.summaryAdvanced',
            );
          }
          return this.translate.instant('features.settings.multipleValues');
        } catch {
          return this.translate.instant('features.settings.multipleValues');
        }
      }
      return this.translate.instant('features.settings.multipleValues');
    }

    const type = this.getParameterType(setting.value, setting.key);

    if (type === 'boolean') {
      const bool = setting.value.toString() === 'true';
      return bool
        ? this.translate.instant('common.status.enabled')
        : this.translate.instant('common.status.disabled');
    }

    if (type === 'select') {
      const optionsKey = `features.settings.parameters.${setting.key}.options.${setting.value}`;
      const labelKey = `features.settings.parameters.${setting.key}.${setting.value}.label`;
      const optionsResult = this.translate.instant(optionsKey);
      if (optionsResult !== optionsKey) return optionsResult;
      const labelResult = this.translate.instant(labelKey);
      if (labelResult !== labelKey) return labelResult;
      return this.translate.instant('common.unknown');
    }

    if (
      setting.key === 'invoiceAmountLimit' ||
      setting.key === 'cashoutTransactionLimit' ||
      setting.key === 'dailyCashoutLimit'
    ) {
      const v = String(setting.value ?? '').trim();
      if (!v || v === '0') {
        return this.translate.instant('features.settings.unlimited');
      }
      return v;
    }

    return setting.value.toString();
  }

  private isObjectOrArray(value: string | object): boolean {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return (
        parsed !== null && (typeof parsed === 'object' || Array.isArray(parsed))
      );
    } catch {
      return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private getParameterType(value: string | object, key?: string): string {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      if (parsed === null) {
        return 'null';
      } else if (Array.isArray(parsed)) {
        return 'array';
      } else {
        return typeof parsed;
      }
    } catch {
      const strValue = value.toString();
      if (strValue === 'true' || strValue === 'false') {
        return 'boolean';
      } else if (!isNaN(Number(strValue))) {
        return 'number';
      } else {
        return 'string';
      }
    }
  }

  onActionClicked(event: {
    row: {
      key: string;
      keyLabel: string;
      description: string;
      value: string;
      setting: RecipientSetting | DomainSetting;
    };
    type: string;
  }) {
    if (event.type === 'edit') {
      this.openEditDialog(event.row.setting);
    }
  }

  private openEditDialog(setting: RecipientSetting | DomainSetting): void {
    const component = this.getComponentFromSettingKey(setting.key);
    if (!component) {
      this.alerts
        .open(
          this.translate.instant('features.settings.editComponentNotFound', {
            key: setting.key,
          }),
          {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          },
        )
        .subscribe();
      return;
    }

    this.dialogService
      .open<boolean>(this.confirmEditDialog, {
        label: this.translate.instant('features.settings.confirmEdit.title'),
        size: 's',
        closeable: true,
        dismissible: true,
        data: {id: this.id, setting},
      })
      .subscribe({
        next: (confirmed: boolean) => {
          if (confirmed) {
            this.openParameterFormDialog(setting);
          }
        },
      });
  }

  /** Opens the parameter form dialog (edit or add). No confirm step. */
  private openParameterFormDialog(
    setting: RecipientSetting | DomainSetting,
  ): void {
    const component = this.getComponentFromSettingKey(setting.key);
    if (!component) {
      this.alerts
        .open(
          this.translate.instant('features.settings.editComponentNotFound', {
            key: setting.key,
          }),
          {
            label: this.translate.instant('common.status.error'),
            appearance: 'error',
          },
        )
        .subscribe();
      return;
    }

    const dialogData: Record<string, unknown> = {
      id: this.id,
      setting,
      editSettingFn: this.editSettingFn,
      entity: this.entity,
      scope: this.scope,
      options: this.getOptionsForSetting(setting.key),
    };
    if (setting.key === 'paymentSplitWhitelist' && this.recipientsService) {
      dialogData['recipientsService'] = this.recipientsService;
    }
    if (
      setting.key === 'globalPaymentSplitRules' &&
      this.globalPaymentSplitRulesDataService
    ) {
      dialogData['globalPaymentSplitRulesDataService'] =
        this.globalPaymentSplitRulesDataService;
    }

    this.dialogService
      .open(new PolymorpheusComponent(component), {
        label: this.translate.instant(
          `features.settings.parameters.${this.parameterI18nKey(
            setting.key,
          )}.label`,
        ),
        size: this.getParameterFormDialogSize(setting.key),
        closeable: true,
        dismissible: false,
        data: dialogData,
      })
      .subscribe();
  }

  /** Wide dialogs for rich editors (page/style/fees); default keeps compact forms readable. */
  private getParameterFormDialogSize(key: string): 's' | 'm' | 'l' | 'auto' {
    switch (key) {
      case 'page':
      case 'style':
      case 'fees':
        return 'l';
      default:
        return 'auto';
    }
  }

  /** Options for the add-parameter select (value + translated label). */
  getAddParameterKeyOptions(): {value: string; label: string}[] {
    return this.availableKeysForAdd.map(key => ({
      value: key,
      label: this.translate.instant(
        `features.settings.parameters.${this.parameterI18nKey(key)}.label`,
      ),
    }));
  }

  onAddParameter(): void {
    const availableKeys = this.availableKeysForAdd;
    if (!availableKeys.length) return;

    this.selectedKeyForAdd = '';
    this.dialogService
      .open<string | undefined>(this.addParameterSelectDialog, {
        label: this.translate.instant(
          'features.settings.addParameterDialog.title',
        ),
        size: 's',
        closeable: true,
        dismissible: true,
        data: {
          availableKeys,
          availableKeyOptions: this.getAddParameterKeyOptions(),
        },
      })
      .subscribe({
        next: (selectedKey: string | undefined) => {
          if (selectedKey) {
            const placeholder = this.createPlaceholderSetting(selectedKey);
            this.openParameterFormDialog(placeholder);
          }
        },
      });
  }

  private getComponentFromSettingKey(key: string): Type<unknown> | null {
    if (
      !this.settingComponents ||
      Object.keys(this.settingComponents).length === 0
    ) {
      return null;
    }

    return (
      (this.settingComponents as Record<string, Type<unknown>>)[key] || null
    );
  }

  /**
   * Returns enum/array of options for boolean components that need predefined options.
   * Add more cases as needed for different setting keys.
   */
  private getOptionsForSetting(key: string): string[] | undefined {
    switch (key) {
      case 'paymentMethods':
        return Object.values(PaymentMethod);
      case 'complianceRequirements':
        return Object.values(DocumentType);
      case 'allowedWithdrawalPixKeyTypes':
        return [...allowedWithdrawalPixKeyTypeOptions];
      default:
        return undefined;
    }
  }
}
